import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

/*
 * Platform-specific fetch adapter.
 *
 * Some preview/staging environments inject a telemetry script that
 * intercepts native fetch() responses and consumes their body streams.
 * This prevents the Supabase SDK from reading response JSON, causing
 * "body stream already read" errors on every request.
 *
 * This adapter routes HTTP through XMLHttpRequest instead, returning
 * a standard Response object the SDK can consume normally.
 *
 * NULL-BODY STATUS HANDLING (BUG FIX):
 *   HTTP status codes 204 (No Content), 205 (Reset Content), and
 *   304 (Not Modified) are "null body" statuses per the Fetch spec.
 *   The Response constructor throws a TypeError if you pass a body
 *   for these statuses:
 *     "Failed to construct 'Response': Response with null body status
 *      cannot have body"
 *   This broke logout (which returns 204) and other SDK operations.
 *   Fix: pass null as the body for these status codes.
 *
 * ➜  When deploying to production without the telemetry script,
 *    remove the `global.fetch` override below and let the SDK use
 *    the browser's native fetch.
 */

/** Status codes that MUST NOT have a response body per HTTP spec. */
const NULL_BODY_STATUSES = new Set([204, 205, 304]);

const platformFetch = (url, options = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url);

    const headers =
      options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : options.headers || {};
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

    xhr.onload = () => {
      const h = new Headers();
      xhr.getAllResponseHeaders().trim().split('\r\n').forEach((line) => {
        const i = line.indexOf(': ');
        if (i > 0) h.append(line.slice(0, i), line.slice(i + 2));
      });

      // Null-body statuses must not carry a body; pass null to avoid
      // "Response with null body status cannot have body" TypeError.
      const body = NULL_BODY_STATUSES.has(xhr.status) ? null : xhr.responseText;

      resolve(new Response(body, { status: xhr.status, statusText: xhr.statusText, headers: h }));
    };
    xhr.onerror = () => reject(new TypeError('Network request failed'));
    xhr.send(options.body || null);
  });

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: { fetch: platformFetch },
});
